import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SOAPNoteMetadata } from '../../types';

const dateValue = (d: string) => new Date(d || 0).getTime();

const stripHtml = (html: string): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

interface ThreadNoteProps {
  patientId: string;
  note: SOAPNoteMetadata;
  isRoot?: boolean;
  metaExtra?: string;
}

const ThreadNote = ({ patientId, note, isRoot = false, metaExtra }: ThreadNoteProps) => {
  const noteType = (note.note_type || '').toUpperCase();
  const dt = note.created_at ? new Date(note.created_at).toLocaleString() : '';
  return (
    <div className='group relative'>
      <Link
        to={`/patients/${patientId}/notes/${note.session_id}`}
        className={[
          'block border rounded-lg p-3 shadow-sm bg-white hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-navy/40',
          isRoot ? 'border-brand-navy/30' : 'border-brand-navy/15',
        ].join(' ')}
      >
        <div className='flex flex-wrap items-center gap-2 justify-between'>
          <div className='flex items-center gap-2 min-w-0'>
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${noteType === 'INITIAL' ? 'bg-brand-navy/10 text-brand-navy' : 'bg-brand-accent/10 text-brand-accent'}`}>
              {noteType || (isRoot ? 'INITIAL' : 'NOTE')}
            </span>
            <span className='text-xs text-gray-500'>{dt}</span>
            {isRoot && metaExtra && <span className='ml-2 text-[10px] text-gray-400'>{metaExtra}</span>}
          </div>
          <span className='text-[10px] text-brand-navy opacity-0 group-hover:opacity-100 transition-opacity'>Open →</span>
        </div>
        {note.consent_excerpt && (
          <div className='mt-1 text-[11px] text-gray-600 line-clamp-2'>{stripHtml(note.consent_excerpt)}</div>
        )}
      </Link>
    </div>
  );
};

interface ThreadGroupProps {
  patientId: string;
  root: SOAPNoteMetadata;
  followUps: SOAPNoteMetadata[];
  isCollapsed: boolean;
  onToggle: () => void;
}

const ThreadGroup = ({ patientId, root, followUps, isCollapsed, onToggle }: ThreadGroupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setMaxHeight(isCollapsed ? 0 : containerRef.current.scrollHeight);
    }
  }, [followUps, isCollapsed]);

  useEffect(() => {
    if (!isCollapsed && containerRef.current) {
      const timer = setTimeout(() => {
        if (containerRef.current) setMaxHeight(containerRef.current.scrollHeight);
      }, 320);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed]);

  return (
    <div className='relative'>
      <div className='flex items-start gap-2'>
        <button
          type='button'
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand thread' : 'Collapse thread'}
          className='mt-1 h-6 w-6 flex items-center justify-center rounded border border-brand-navy/15 bg-white text-gray-500 hover:bg-brand-navy/5 focus:outline-none focus:ring-2 focus:ring-brand-accent'
        >
          <span className={'transition-transform text-xs ' + (isCollapsed ? '' : 'rotate-90')}>▶</span>
        </button>
        <div className='flex-1'>
          <ThreadNote
            patientId={patientId}
            note={root}
            isRoot
            metaExtra={followUps.length ? `${followUps.length} follow-up${followUps.length > 1 ? 's' : ''}` : 'No follow-ups'}
          />
          {followUps.length > 0 && (
            <div
              ref={containerRef}
              className='mt-4 ml-4 pl-4 border-l border-brand-navy/15 space-y-4 overflow-hidden transition-all duration-300 ease-in-out'
              style={{ maxHeight, opacity: isCollapsed ? 0 : 1, pointerEvents: isCollapsed ? 'none' : 'auto' }}
              aria-hidden={isCollapsed}
            >
              {followUps.map((n) => (
                <ThreadNote key={n.session_id} patientId={patientId} note={n} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface NotesTimelineProps {
  patientId: string;
  notes: SOAPNoteMetadata[];
}

export const NotesTimeline = ({ patientId, notes }: NotesTimelineProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  if (!notes || notes.length === 0) {
    return <div className='text-xs text-gray-400'>No notes yet.</div>;
  }

  // Group by case_id — all notes in the same batman Case belong to one tree.
  // New tree starts only when a new INITIAL note begins a new case.
  const groupsMap: Record<string, SOAPNoteMetadata[]> = {};
  for (const n of notes) {
    const key = n.case_id ?? n.session_id;
    if (!groupsMap[key]) groupsMap[key] = [];
    groupsMap[key].push(n);
  }

  const threads = Object.entries(groupsMap).map(([caseId, items]) => {
    const chain = items.slice().sort((a, b) => dateValue(a.created_at) - dateValue(b.created_at));
    // Root is the INITIAL note; fall back to oldest note in the group
    const root = chain.find((n) => n.note_type.toUpperCase() === 'INITIAL') ?? chain[0];
    return { root, chain, rootId: caseId };
  });
  // Newest case first
  threads.sort((a, b) => dateValue(b.root.created_at) - dateValue(a.root.created_at));

  return (
    <div className='space-y-8'>
      {threads.map(thread => {
        const followUps = thread.chain.filter(n => n.session_id !== thread.root.session_id);
        return (
          <ThreadGroup
            key={thread.rootId}
            patientId={patientId}
            root={thread.root}
            followUps={followUps}
            isCollapsed={!!collapsed[thread.rootId]}
            onToggle={() => toggle(thread.rootId)}
          />
        );
      })}
    </div>
  );
};

export default NotesTimeline;
